import * as React from 'react';
import { Button } from '@org/ui';
import { Label } from '@org/ui';
import { Switch } from '@org/ui';
import { DatePicker } from '@org/ui';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@org/ui';
import { useTranslation } from 'react-i18next';
import { WEEKDAY_OPTIONS, parseDateOnly, toDateOnlyString } from '@org/utils-client';
import type { EventFormValues } from './event-form.helpers';
import type { UseFormReturn } from 'react-hook-form';

type RecurrenceControlsProps = {
  methods: UseFormReturn<EventFormValues>;
  mode: 'create' | 'edit';
  scopedEditingAvailable: boolean;
};

export function RecurrenceControls({ methods, mode, scopedEditingAvailable }: RecurrenceControlsProps) {
  const { t } = useTranslation('admin');
  const { watch, setValue } = methods;

  const [
    isRecurring,
    selectedWeekdays = [],
    recurrenceEndDate,
    recurrenceFrequency = 'weekly',
    applyScope,
    forceResetAllAssociations,
  ] = watch([
    'isRecurring',
    'recurrenceWeekdays',
    'recurrenceEndDate',
    'recurrenceFrequency',
    'applyScope',
    'forceResetAllAssociations',
  ]);

  const isSeriesScope = applyScope === 'series';
  const canScope = mode === 'edit' && scopedEditingAvailable;
  const showRecurrenceControls = canScope ? isSeriesScope : isRecurring;

  // Set initial applyScope when in edit mode with scoping
  React.useEffect(() => {
    if (canScope && applyScope === undefined) {
      setValue('applyScope', 'occurrence', { shouldDirty: false });
    }
  }, [applyScope, canScope, setValue]);

  const setField = <K extends keyof EventFormValues>(key: K, value: EventFormValues[K]) =>
    setValue(key, value, { shouldDirty: true });

  const toggleWeekday = (code: string) => {
    const next = selectedWeekdays.includes(code)
      ? selectedWeekdays.filter(d => d !== code)
      : [...selectedWeekdays, code];
    setField('recurrenceWeekdays', next as EventFormValues['recurrenceWeekdays']);
  };

  const handleFrequencyChange = (value: string) => {
    const nextFrequency = value as 'daily' | 'weekly' | 'monthly';
    setField('recurrenceFrequency', nextFrequency);
    if (nextFrequency === 'daily' || nextFrequency === 'monthly') {
      setField('recurrenceWeekdays', []);
    }
  };

  const resetRecurrence = () => {
    setField('recurrenceWeekdays', []);
    setField('recurrenceEndDate', null);
    setField('recurrenceFrequency', 'weekly');
  };

  const handleScopeToggle = (checked: boolean) => {
    const nextScope = checked ? 'series' : 'occurrence';
    setField('applyScope', nextScope);
    setField('isRecurring', checked);
  };

  const handleRecurringToggle = (checked: boolean) => {
    setField('isRecurring', checked);
    if (!checked) resetRecurrence();
  };

  return (
    <div className="space-y-4">
      {/* Scope toggle (edit mode with series) */}
      {canScope && (
        <div className="flex items-center justify-between rounded-md border border-border px-3 py-3">
          <div>
            <p className="text-sm font-medium text-foreground">
              {t('events.form.helper.scopeTitle')}
            </p>
            <p className="text-xs text-muted-foreground">
              {t('events.form.helper.scopeDescription')}
            </p>
          </div>
          <Switch checked={isSeriesScope} onCheckedChange={handleScopeToggle} />
        </div>
      )}

      {/* Recurring toggle (create mode) */}
      {!canScope && (
        <div className="flex items-center justify-between rounded-md border border-border px-3 py-3">
          <div>
            <p className="text-sm font-medium text-foreground">
              {t('events.form.labels.isRecurring')}
            </p>
            <p className="text-xs text-muted-foreground">
              {t('events.form.helper.recurrenceToggle')}
            </p>
          </div>
          <Switch checked={isRecurring} onCheckedChange={handleRecurringToggle} />
        </div>
      )}

      {/* Disabled state message for edit mode */}
      {canScope && !isSeriesScope && (
        <p className="text-sm text-muted-foreground">
          {t('events.form.helper.scopeDisabled')}
        </p>
      )}

      {/* Recurrence options */}
      {showRecurrenceControls && (
        <div className="space-y-4">
          {/* Frequency selector */}
          <div className="space-y-2">
            <Label className="text-xs uppercase text-muted-foreground">
              {t('events.form.labels.frequency')}
            </Label>
            <Select value={recurrenceFrequency} onValueChange={handleFrequencyChange}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">{t('events.form.options.frequency.weekly')}</SelectItem>
                <SelectItem value="daily">{t('events.form.options.frequency.daily')}</SelectItem>
                <SelectItem value="monthly">{t('events.form.options.frequency.monthly')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Weekday picker (weekly only) */}
          {recurrenceFrequency === 'weekly' && (
            <div>
              <Label className="text-xs uppercase text-muted-foreground">
                {t('events.form.labels.repeatOn')}
              </Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {WEEKDAY_OPTIONS.map(weekday => (
                  <Button
                    key={weekday.code}
                    type="button"
                    size="sm"
                    variant={selectedWeekdays.includes(weekday.code) ? 'default' : 'outline'}
                    onClick={() => toggleWeekday(weekday.code)}
                  >
                    {weekday.label.slice(0, 3)}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* End date picker */}
          <div className="space-y-2">
            <Label htmlFor="recurrenceEnd" className="text-xs uppercase text-muted-foreground">
              {t('events.form.labels.repeatUntil')}
            </Label>
            <DatePicker
              value={recurrenceEndDate ? parseDateOnly(recurrenceEndDate) : undefined}
              onChange={date => setField('recurrenceEndDate', date ? toDateOnlyString(date) : null)}
              placeholder={t('events.form.placeholders.recurrenceEnd')}
            />
          </div>

          {/* Reset associations toggle (edit mode series only) */}
          {mode === 'edit' && isSeriesScope && (
            <div className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 px-3 py-3">
              <div>
                <p className="text-sm font-medium text-amber-900">
                  {t('events.form.labels.resetAssociations')}
                </p>
                <p className="text-xs text-amber-700">
                  {t('events.form.helper.resetAssociationsDescription')}
                </p>
              </div>
              <Switch
                checked={forceResetAllAssociations ?? false}
                onCheckedChange={checked => setField('forceResetAllAssociations', checked)}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
